import { Component, OnInit, OnDestroy } from '@angular/core';
import { ConfirmationService } from 'src/app/modules/shared/services/confirmation.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { ReportingService } from '../../../services/reporting.service';
import { Employee } from 'src/app/modules/shared/models/employee';
import { ReportItemViewModel } from '../../../viewModels/reportItemViewModel';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { T } from 'src/assets/i18n/translation-keys';
import { TranslateService } from '@ngx-translate/core';
import { WtValidators } from 'src/app/modules/shared/reactiveValidators/wtValidators';
import { ReportExportRequestViewModel } from '../../../viewModels/exportPdfViewModel';
import { AlertService } from 'src/app/modules/shared/services/alert.service';


@Component({
    selector: 'app-pdf-export-report-modal',
    templateUrl: './pdf-export-report-modal.component.html',
    styleUrls: ['./pdf-export-report-modal.component.scss'],
    standalone: false
})
export class PdfExportReportModalComponent implements OnInit, OnDestroy {
  public triggerErrors: boolean;
  public currentEmployee: Employee;
  public reportItem: ReportItemViewModel;
  public reportSectionIndex: number = 0;
  public singleSectionExport: boolean;
  public form: UntypedFormGroup;
  public subscription = new Subscription();
  public loading: boolean;
  public readonly T = T;

  constructor(
    private readonly bsModalRef: BsModalRef,
    private readonly confirmationService: ConfirmationService,
    private readonly authenticationService: AuthenticationService,
    private readonly reportingService: ReportingService,
    private fb: UntypedFormBuilder,
    private readonly translateService: TranslateService,
    private readonly wtValidators: WtValidators,
    private readonly alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      reportTitle: ['', {validators: [Validators.required, this.wtValidators.textInput(3, 80, WtValidators.RESTRICTED_CHARS.DEFAULT)]}],
      reportDescription: ['', {validators: [this.wtValidators.textInput(0, 250, WtValidators.RESTRICTED_CHARS.DEFAULT)]}],
    });

    if (this.reportItem) {
      if (this.reportItem.title != 'New Report') {
        this.form.get('reportTitle').setValue(this.reportItem.title);
      }
      this.form.get('reportDescription').setValue(this.reportItem.description);
    }

    this.currentEmployee = this.authenticationService.getCurrentEmployee();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onApply() {
    if (!this.form.valid) {
      this.triggerErrors = true;
      return;
    }

    this.loading = true;

    // Get title and description from form
    const title = this.form.controls.reportTitle.value.trim();
    const description = this.form.controls.reportDescription?.value?.trim() || '';

    // Create export request payload
    const exportRequest = new ReportExportRequestViewModel();
    exportRequest.reportItemId = this.reportItem.id;
    exportRequest.title = title;
    exportRequest.description = description;
    exportRequest.showFilters = false; // Always false for now as per requirements

    if (this.singleSectionExport) {
      const singleSection = this.reportItem.reportItemSections.find((x) => x.viewOrder == this.reportSectionIndex);
      exportRequest.reportItemSectionId = singleSection.id;
    } else {
      exportRequest.reportItemSectionId = 0; // 0 means export all sections
    }

    // Use the new POST endpoint for PDF export - returns 201 Accepted, no file data
    this.reportingService.exportReport(exportRequest).subscribe({
      next: () => {
        // The POST endpoint returns 201 Accepted and processes export as background job
        // Show success message to user
        this.alertService.success(
          this.translateService.instant(T.reports.export_to_pdf),
          this.translateService.instant(T.common.go_to) + ' ' +
          this.translateService.instant(T.settings.document_manager) + ' ' +
          this.translateService.instant(T.reports.see_reports_in_document_manager)
        );
        this.loading = false;
        this.bsModalRef.hide();
      },
      error: () => {
        // Handle error case
        this.alertService.error(
          this.translateService.instant(T.common.there_was_a_problem),
          this.translateService.instant(T.common.something_happened_try_again)
        );
        this.loading = false;
      }
    });
  }

  confirmClose() {
    this.confirmationService.confirmThis(
      this.translateService.instant(T.reports.confirm_leave_without_exporting),
      () => {
        this.bsModalRef.hide();
      },
      () => {},
      this.translateService.instant(T.common.confirm),
      false,
      this.translateService.instant(T.common.stay),
      this.translateService.instant(T.common.leave),
      'danger'
    );
  }
}
